import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import AuthContext from "../Context/AuthContext";

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function Guidelines() {
  const history = useHistory();
  const location = useLocation();
  const { userData } = useContext(AuthContext);

  const [ruleData, setRuleData] = useState([]);
  const [ruleCampData, setRuleCampData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 200);

  // "account" veya "campaign"
  const [guidelineTab, setGuidelineTab] = useState("account");

  const user = userData?.user?.user;
  const role = user?.role || "User";

  const tabsAdmin = [
    { key: "accounts", label: "Accounts", path: "/accounts" },
    { key: "guidelines", label: "Guidelines", path: "/guidelines" },
    { key: "yourteam", label: "Your Team", path: "/yourteam" },
    { key: "howto", label: "How to Use", path: "/howto" },
  ];
  const tabsUser = [
    { key: "guidelines", label: "Guidelines", path: "/guidelines" },
    { key: "howto", label: "How to Use", path: "/howto" },
  ];
  const tabs = role === "Admin" ? tabsAdmin : tabsUser;

  const activeKey =
    tabs.find((t) => location.pathname.startsWith(`/${t.key}`))?.key || "guidelines";

  useEffect(() => {
    let mounted = true;
    async function fetchAll() {
      try {
        setIsLoading(true);
        setErr(null);
        const name = user?.name;
        const id = user?._id;
        const requests = [];

        if (name) {
          requests.push(axios.post("https://act-api.vercel.app/findamember", { name }));
        }
        if (id) {
          requests.push(axios.post("https://act-api.vercel.app/findacamprule", { id }));
        }

        const [memberRes, campRes] = await Promise.all([
          requests[0] || Promise.resolve({ data: [] }),
          requests[1] || Promise.resolve({ data: [] }),
        ]);

        if (!mounted) return;
        setRuleData(Array.isArray(memberRes.data) ? memberRes.data : []);
        setRuleCampData(Array.isArray(campRes.data) ? campRes.data : []);
      } catch (e) {
        if (!mounted) return;
        setErr("Veriler alınırken bir sorun oluştu. Lütfen tekrar deneyin.");
      } finally {
        mounted && setIsLoading(false);
      }
    }
    fetchAll();
    return () => { mounted = false; };
  }, [user?.name, user?._id]);

  const filteredAccountGuidelines = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const base = ruleData || [];
    const filtered = term
      ? base.filter((i) => (i?.name || "").toLowerCase().includes(term))
      : base;
    return filtered.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [ruleData, debouncedSearch]);

  const filteredCampaignGuidelines = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const base = Array.isArray(ruleCampData) ? ruleCampData : [];
    const filtered = term ? base.filter((i) => (i || "").toLowerCase().includes(term)) : base;
    return filtered.slice().sort((a, b) => (a || "").localeCompare(b || ""));
  }, [ruleCampData, debouncedSearch]);

  function go(path) {
    history.push(path);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Üst Bar */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between py-3 gap-3">
            {/* Ana Sekmeler */}
            <div className="w-full sm:w-auto">
              <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
                {tabs.map((t) => {
                  const active = activeKey === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => go(t.path)}
                      className={[
                        "px-4 py-2 rounded-lg text-sm font-semibold transition",
                        active
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
                      ].join(" ")}
                      aria-current={active ? "page" : undefined}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Arama + Yeni */}
            <div className="flex w-full sm:w-auto items-center gap-2">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search guidelines…"
                  aria-label="Search guidelines"
                  className="w-full sm:w-80 rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
              </div>

              <button
                onClick={() => go("/newguideline")}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 transition"
                title="Create new guideline"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                New Guideline
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <header className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Guidelines</h1>
          <p className="text-slate-500 text-sm">
            {debouncedSearch ? `Results for "${debouncedSearch}"` : "All guidelines for your workspace"}
          </p>
        </header>

        {/* İç Sekmeler: Account / Campaign */}
        <div className="mb-6">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
            <button
              onClick={() => setGuidelineTab("account")}
              className={[
                "px-5 py-2 rounded-lg text-sm font-semibold transition",
                guidelineTab === "account"
                  ? "bg-slate-800 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
              ].join(" ")}
            >
              Account Guidelines
            </button>
            <button
              onClick={() => setGuidelineTab("campaign")}
              className={[
                "px-5 py-2 rounded-lg text-sm font-semibold transition",
                guidelineTab === "campaign"
                  ? "bg-slate-800 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
              ].join(" ")}
            >
              Campaign Guidelines
            </button>
          </div>
        </div>

        {/* Hata */}
        {err && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* Yükleniyor iskeleti */}
        {isLoading && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-0">
                <div className="h-9 w-9 rounded-full bg-slate-200 animate-pulse shrink-0" />
                <div className="flex-1">
                  <div className="h-4 w-40 bg-slate-200 rounded animate-pulse mb-1.5" />
                  <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
                </div>
                <div className="h-8 w-8 rounded-full bg-slate-200 animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {/* ACCOUNT GUIDELINES */}
        {!isLoading && !err && guidelineTab === "account" && (
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                Account Guidelines
              </h2>
              <span className="text-xs text-slate-500">{filteredAccountGuidelines.length} items</span>
            </div>

            {filteredAccountGuidelines.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-slate-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197M16.5 10.5a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" />
                  </svg>
                </div>
                <h3 className="font-semibold">Sonuç bulunamadı</h3>
                <p className="text-sm text-slate-500">Farklı bir arama deneyebilir veya yeni bir guideline oluşturabilirsiniz.</p>
                <button
                  onClick={() => go("/newguideline")}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200 transition"
                >
                  New Guideline
                </button>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                {filteredAccountGuidelines.map((item, idx) => (
                  <div
                    key={`${item.platform}-${item.name}`}
                    className={[
                      "flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors",
                      idx !== filteredAccountGuidelines.length - 1 ? "border-b border-slate-100" : "",
                    ].join(" ")}
                  >
                    <img
                      src={item.imageURL}
                      alt={item.name || "Account"}
                      className="h-9 w-9 rounded-full object-cover ring-2 ring-slate-100 shrink-0"
                      onError={(e) => {
                        e.currentTarget.src =
                          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='36' height='36'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='54%' font-size='13' font-family='Arial' text-anchor='middle' fill='%236b7280'>NA</text></svg>";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{item.name || "Unnamed"}</p>
                      <p className="text-xs text-slate-500 truncate">{item.platform || "—"}</p>
                    </div>
                    {item.platform && (
                      <span className="hidden sm:inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 shrink-0">
                        {item.platform}
                      </span>
                    )}
                    <a
                      href={`/accountrules/${encodeURIComponent(item.platform)}/${item.name}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 shrink-0"
                      aria-label={`Open rules for ${item.name}`}
                      title="Open details"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* CAMPAIGN GUIDELINES */}
        {!isLoading && !err && guidelineTab === "campaign" && (
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                Campaign Guidelines
              </h2>
              <span className="text-xs text-slate-500">{filteredCampaignGuidelines.length} items</span>
            </div>

            {filteredCampaignGuidelines.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-slate-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197M16.5 10.5a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" />
                  </svg>
                </div>
                <h3 className="font-semibold">Sonuç bulunamadı</h3>
                <p className="text-sm text-slate-500">Aramanızı değiştirin veya yeni bir guideline oluşturun.</p>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                {filteredCampaignGuidelines.map((name, idx) => (
                  <div
                    key={name}
                    className={[
                      "flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors",
                      idx !== filteredCampaignGuidelines.length - 1 ? "border-b border-slate-100" : "",
                    ].join(" ")}
                  >
                    <div className="h-9 w-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <svg className="h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-8 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
                      <p className="text-xs text-slate-500">Campaign</p>
                    </div>
                    <a
                      href={`/campaignrules/${encodeURIComponent(name)}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 shrink-0"
                      aria-label={`Open campaign rules for ${name}`}
                      title="Open details"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
